import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { getCardInfo, getCardNonPsaListings } from '@/api/card';
import Breadcrumb from '@/components/Breadcrumb';
import Button from '@/components/Button';
import RarityBadge from '@/components/RarityBadge';
import { useTranslation } from 'react-i18next';
import { useState, useEffect, useCallback } from 'react';
import Checkbox from '@/components/Checkbox';
import Sort from '@/components/Sort';
import Select from '@/components/Select';
import CardItemView from '@/components/CardItemView';
import { useLocation } from 'react-router-dom';
import { CardSkeleton, CardInfoSkeleton, CardListingFiltersSkeleton } from '@/components/Skeleton';
import useMinimumLoadingTime from '@/hooks/useMinimumLoadingTime';
import dondonGrey from '@/assets/dondon_grey.svg';
import usePageSize from '@/hooks/usePageSize';
import useAuthStore from '@/stores/authStore';
import { getProductThumbnailUrl } from '@/utils/imageUtils';

const SORT = {
  price: "PRICE",
  release_date: "CREATION_TIME"
};

const CardListingPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const [onSaleOnly, setOnSaleOnly] = useState(false);
  const [condition, setSelectedCondition] = useState(location.state?.filter || 'ALL');
  const [sortConfig, setSortConfig] = useState({ field: SORT.release_date, direction: 'DESC' });
  const pageSize = usePageSize();
  const { data: cardInfo, isLoading: isLoadingCard } = useQuery({
    queryKey: ['card', id],
    queryFn: () => getCardInfo(id),
  });

  const { 
    data: listings, 
    isLoading: isLoadingListings, 
    hasNextPage, 
    fetchNextPage, 
    isFetchingNextPage 
  } = useInfiniteQuery({
    queryKey: ['cardNonPsaListings', id, onSaleOnly, sortConfig, pageSize, condition],
    queryFn: ({ pageParam = null}) => getCardNonPsaListings(id, onSaleOnly, condition,sortConfig.field, sortConfig.direction, pageParam, pageSize),
    getNextPageParam: (lastPage) => lastPage.cursor,
    initialPageParam: null,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });

  // Apply minimum loading time to loading states
  const showCardLoading = useMinimumLoadingTime(isLoadingCard);
  const showListingsLoading = useMinimumLoadingTime(isLoadingListings && !listings);

  const breadcrumbItems = [
    { label: t('navigation.home'), href: '/' },
    { label: t('hot_items'), href: '/' },
    { label: `${cardInfo?.data?.localizedRawName || cardInfo?.data?.localizedName || ''}${cardInfo?.data?.type !== 'CARD_BOX' && cardInfo?.data?.rarityInfo?.sub?.name ? ` (${cardInfo.data.rarityInfo.sub.name})` : ''}`, href: `/card/${id}` },
    { label: t('no_psa_rating') },
  ];

  const handleLoadMore = () => {
    if (hasNextPage) {
      fetchNextPage();
    }
  };

  // Track pages loaded for auto load more
  const pagesLoaded = listings?.pages?.length || 0;

  // Handle infinite scroll for first 3 pages
  const handleScroll = useCallback(() => {
    if (isFetchingNextPage || !hasNextPage || pagesLoaded >= 1) return;
    
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const windowHeight = window.innerHeight;
    const docHeight = document.documentElement.scrollHeight;
    
    // Trigger load more when user scrolls to within 200px of the bottom
    if (scrollTop + windowHeight >= docHeight - 200) {
      handleLoadMore();
    }
  }, [isFetchingNextPage, hasNextPage, pagesLoaded, handleLoadMore]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const handleOnSaleOnlyChange = (checked) => {
    setOnSaleOnly(checked);
  };

  const handleSort = (sortItem, sortDirection) => {
    console.log('Sorting by:', sortItem, sortDirection);
    setSortConfig({
      field: SORT[sortItem.value],
      direction: sortDirection.toUpperCase()
    });
  };

  const handleCreateListing = () => {
    navigate(`/card/${id}/sell?NON_PSA=1`);
  };

  // Condition options
  const conditionOptions = [
    { value: 'ALL', label: t('sell.product_info.conditions.all') },
    { value: 'MINT', label: t('sell.product_info.conditions.mint') },
    { value: 'NEAR_MINT', label: t('sell.product_info.conditions.near_mint') },
    { value: 'FAIR', label: t('sell.product_info.conditions.fair') },
    { value: 'POOR', label: t('sell.product_info.conditions.poor') },
  ];

  const handleConditionChange = (condition) => {
    setSelectedCondition(condition);
  };

  // Function to get translated condition label
  const getTranslatedCondition = (condition) => {
    if (!condition) return '';
    return t(`sell.product_info.conditions.${condition.toLowerCase()}`);
  };

  // Flatten all pages of data
  const allListings = listings?.pages?.flatMap(page => page.data) || [];

  // Check scroll position after loading completes
  useEffect(() => {
    if (!isFetchingNextPage && hasNextPage && allListings.length > 0 && pagesLoaded < 3) {
      // Small delay to ensure DOM is updated
      const timer = setTimeout(() => {
        handleScroll();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isFetchingNextPage, hasNextPage, allListings.length, pagesLoaded, handleScroll]);

  if (showCardLoading) {
    return (
      <div className="container p-8">
        <Breadcrumb items={[
          { label: t('navigation.home'), href: '/' },
          { label: t('hot_items'), href: '/' },
          { label: '', href: '#' },
          { label: t('no_psa_rating') },
        ]} />

        <CardInfoSkeleton />
        <CardListingFiltersSkeleton />
        
        <div className="grid grid-cols-5 max:grid-cols-6 gap-6">
          {Array.from({ length: 12 }, (_, i) => (
            <CardSkeleton key={`skeleton-${i}`} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="container p-8">
      <Breadcrumb items={breadcrumbItems} />

      {/* Card Info */}
      <div className="w-full relative bg-white rounded-md border border-[#dddddd] p-6 mt-6">
        <div className="flex gap-8">
          <div className="w-[88px] h-[88px] bg-white rounded flex justify-center items-center shrink-0">
            <img 
              className="w-full h-full rounded-sm object-contain"
              src={getProductThumbnailUrl(cardInfo?.data?.images?.[0]?.url)}
              alt={cardInfo?.data?.localizedName}
            />
          </div>
          <div className="flex flex-col gap-1">
            <h1 className="text-2xl font-bold text-[#14151a] leading-9">
              {cardInfo?.data?.localizedRawName || cardInfo?.data?.localizedName}
              {cardInfo?.data?.rarityInfo?.sub?.name && `(${cardInfo.data.rarityInfo.sub.name})`}
            </h1>
            <div className="flex flex-col justify-start items-start gap-1">
              <div className="inline-flex justify-start items-center gap-1">
                <span className="text-[#111111] text-sm leading-[21px]">
                  {cardInfo?.data?.rarityInfo?.main && (
                    <RarityBadge rarity={cardInfo.data.rarityInfo.main} className="mr-1" />
                  )}
                  {cardInfo?.data?.rawCode && (
                    <span className="h-[18px] px-1 bg-[#f4f4f4] rounded-[3px] outline outline-[0.50px] outline-offset-[-0.50px] outline-[#dddddd] mr-1 inline-flex justify-center items-center align-middle">
                      <span className="text-[#111111] text-sm font-normal leading-none">
                        {cardInfo.data.rawCode}
                      </span>
                    </span>
                  )}
                  <span className="align-middle">{cardInfo?.data?.notes}</span>
                </span>
              </div>
              {cardInfo?.data?.brand?.localizedName && (
                <div className="text-[#666666] text-sm">
                  {cardInfo.data.brand.name}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="py-6">
        <div className="w-full flex justify-between items-center">
          <Select
            value={condition}
            onValueChange={handleConditionChange}
            options={conditionOptions}
            className="w-60"
          />
          <div className="flex items-center gap-4">
            <div className="bg-[#f4f4f4] rounded-[18px] px-[18px] py-2 flex items-center">
              <div className="flex items-center gap-1.5">
                <Checkbox 
                  checked={onSaleOnly}
                  onCheckedChange={handleOnSaleOnlyChange}
                />
                <div className="text-[#111111] text-sm font-normal">
                  {t('show_available_only')}
                </div>
              </div>
            </div>
            <Sort
              items={[
                { label: t('listing.release_date'), value: 'release_date' },
                { label: t('price'), value: 'price' }
              ]}
              onChange={handleSort}
              defaultSortIndex={0}
              defaultDirection="desc"
            />
          </div>
        </div>
      </div>

      {/* Listings Grid or Empty State */}
      {showListingsLoading ? (
        <div className="grid grid-cols-5 max:grid-cols-6 gap-6">
          {Array.from({ length: 12 }, (_, i) => (
            <CardSkeleton key={`skeleton-${i}`} />
          ))}
        </div>
      ) : allListings.length > 0 ? (
        <>
          <div className="grid grid-cols-5 max:grid-cols-6 gap-6">
            {allListings.map((listing) => (
              <CardItemView
                key={listing.id}
                id={listing.id}
                type="NON_PSA_SINGLE_CARD"
                image={listing?.images?.front?.url || listing?.product?.images?.[0]?.url}
                name={listing.product.localizedRawName}
                price={listing.price}
                condition={getTranslatedCondition(listing.condition)}
                sold={listing.status === 'SOLD'}
                brand={listing.product?.brand?.localizedName}
                code={listing.product?.code}
                rawCode={listing.product?.rawCode}
                set={listing.product?.set}
                rarityInfo={listing.product?.rarityInfo}
                isListing={true}
                customRightText=""
              />
            ))}
            
            {/* Show skeleton cards when loading more */}
            {isFetchingNextPage && Array.from({ length: 20 }, (_, i) => (
              <CardSkeleton key={`skeleton-more-${i}`} />
            ))}
          </div>

          {/* Load More Button - only show after 3 pages */}
          {pagesLoaded >= 1 && hasNextPage && !isFetchingNextPage && (
            <div className="flex justify-center mt-6">
              <Button 
                variant="primary" 
                onClick={handleLoadMore}
              >
                {t('home.actions.load_more')}
              </Button>
            </div>
          )}
        </>
      ) : (
        <div className="flex flex-col items-center justify-center py-16">
          <img src={dondonGrey} alt="No listings" className="w-[120px] h-[80px] mb-6" />
          <p className="text-[#666666] text-base mb-6">{t('card_listing.empty_state')}</p>
          {(!cardInfo?.data?.onlyOfficial || (user && user?.roles?.includes('OFFICIAL'))) && <Button variant="primary" onClick={handleCreateListing}>
            {t('card_listing.create_listing_button')}
          </Button>}
        </div>
      )}
    </div>
  );
};

export default CardListingPage; 
